"use client";

import { avatarColors } from "@/config/constants";
import { cn, formatUsername } from "@/lib/utils";
import { IPFSMediaRenderer, useProfile } from "@b3dotfun/sdk/global-account/react";
import Avatar from "boring-avatars";
import { useCallback, useState } from "react";

interface UserAvatarProps {
  /** Direct avatar URL (takes precedence over profile fetch) */
  avatarUrl?: string | null;
  /** Display name for fallback initial */
  name?: string | null;
  /** b3GlobalId to fetch profile data (if avatarUrl not provided) */
  b3GlobalId?: string | null;
  /** Ethereum address to fetch profile data (alternative to b3GlobalId) */
  address?: string;
  /** Width of the avatar (also used as size for compatibility) */
  width?: number;
  /** Height of the avatar */
  height?: number;
  /** Size (alternative to width/height for square avatars) */
  size?: number;
  /** Whether this is an anonymous user (shows "A" fallback) */
  isAnonymous?: boolean;
  /** Whether to include username text next to avatar */
  includeUsername?: boolean;
  /** Whether to include avatar image */
  includeAvatar?: boolean;
  /** Custom className for the container */
  className?: string;
  /** Custom className for the avatar image */
  imageClassName?: string;
  /** Custom className for the username text */
  textClassName?: string;
  /** Fallback name if no profile found */
  fallbackName?: string;
}

/**
 * A unified avatar component that renders user avatars consistently across the app.
 * This component consolidates avatar rendering and deprecates ENSAvatar.
 *
 * Usage modes:
 * 1. Direct: Pass avatarUrl and name directly (fastest, no API call)
 * 2. Profile fetch: Pass b3GlobalId or address to auto-fetch profile data
 * 3. Anonymous: Pass isAnonymous=true for anonymous user styling
 * 4. With username: Pass includeUsername=true to show name next to avatar
 *
 * @example
 * // Direct mode - when you already have profile data
 * <UserAvatar avatarUrl={profile.avatar} name={profile.name} width={32} height={32} />
 *
 * @example
 * // Profile fetch mode - auto-fetches by b3GlobalId
 * <UserAvatar b3GlobalId={userId} width={32} height={32} />
 *
 * @example
 * // With username display
 * <UserAvatar address={address} size={24} includeAvatar includeUsername textClassName="text-sm" />
 *
 * @example
 * // Anonymous mode
 * <UserAvatar isAnonymous width={32} height={32} />
 */
export default function UserAvatar({
  avatarUrl,
  name,
  b3GlobalId,
  address,
  width,
  height,
  size,
  isAnonymous = false,
  includeUsername = false,
  includeAvatar = true,
  className,
  imageClassName,
  textClassName,
  fallbackName = "User",
}: UserAvatarProps) {
  // Normalize size - support both size and width/height props
  const finalWidth = size || width || 40;
  const finalHeight = size || height || 40;

  // If b3GlobalId looks like an Ethereum address, treat it as an address for profile lookup
  const b3GlobalIdIsAddress = !!b3GlobalId && b3GlobalId.startsWith("0x") && b3GlobalId.length === 42;
  const effectiveB3GlobalId = b3GlobalIdIsAddress ? undefined : b3GlobalId;
  const effectiveAddress = address || (b3GlobalIdIsAddress ? b3GlobalId : undefined);

  // Fetch profile if b3GlobalId or address provided and not anonymous
  const shouldFetchProfile = !isAnonymous && !avatarUrl && (effectiveB3GlobalId || effectiveAddress);
  const { data: profileData } = useProfile({
    b3GlobalId: shouldFetchProfile && effectiveB3GlobalId ? effectiveB3GlobalId : undefined,
    address: shouldFetchProfile && effectiveAddress ? effectiveAddress : undefined,
    name: undefined,
  });

  // Resolve avatar and name from props or fetched profile
  const resolvedAvatarUrl = avatarUrl || profileData?.avatar;
  const resolvedName =
    name || profileData?.displayName || profileData?.name || effectiveAddress || effectiveB3GlobalId || fallbackName;

  // Track broken avatar images so we can fall back to generated avatars
  const [imgError, setImgError] = useState(false);
  const handleImgError = useCallback(() => setImgError(true), []);

  // Deterministic seed for beam avatar.
  // b3GlobalId is preferred over resolvedName because resolvedName can differ
  // between viewers (e.g. "max-dev-2" for self vs "Team member" for others),
  // causing the same user to render different beam avatars on each side.
  const avatarSeed = address || b3GlobalId || resolvedName || "user";

  // Anonymous user styling
  if (isAnonymous) {
    const avatarElement = (
      <div
        className={cn(
          "bg-ds-neutral-300 text-ds-text-subtle dark:bg-ds-neutral-700 dark:text-ds-text-disabled flex items-center justify-center rounded-full text-xs font-semibold",
          imageClassName,
        )}
        style={{ width: finalWidth, height: finalHeight, fontSize: Math.max(8, finalWidth * 0.4) }}
      >
        A
      </div>
    );

    if (includeUsername) {
      return (
        <div className={cn("flex items-center gap-2", className)}>
          {includeAvatar && avatarElement}
          <span className={cn("font-primary", textClassName)}>Anonymous</span>
        </div>
      );
    }

    return avatarElement;
  }

  // Avatar element - shows image, falls back to beam avatar on error or missing URL
  const avatarElement = includeAvatar && (
    <div
      className={cn("flex items-center justify-center overflow-hidden rounded-full", imageClassName)}
      style={{ width: finalWidth, height: finalHeight, minWidth: finalWidth, minHeight: finalHeight }}
    >
      {resolvedAvatarUrl && !imgError ? (
        <div onErrorCapture={handleImgError} className="h-full w-full">
          <IPFSMediaRenderer
            src={resolvedAvatarUrl}
            width={finalWidth}
            height={finalHeight}
            alt={resolvedName || ""}
            className="h-full w-full object-cover"
            controls={false}
            style={{}}
            onError={() => {}}
          />
        </div>
      ) : (
        <Avatar name={avatarSeed} variant="beam" size={finalWidth} colors={avatarColors} />
      )}
    </div>
  );

  // If includeUsername is true, render avatar + username
  if (includeUsername) {
    // Format address for display if resolvedName is an Ethereum address
    const isAddr = typeof resolvedName === "string" && resolvedName.startsWith("0x") && resolvedName.length === 42;
    const nameToDisplay = isAddr
      ? `${resolvedName.slice(0, 6)}...${resolvedName.slice(-4)}`
      : formatUsername(resolvedName) || resolvedName;

    return (
      <div className={cn("flex items-center gap-2", className)}>
        {avatarElement}
        <span className={cn("font-primary", textClassName)} title={resolvedName}>
          {nameToDisplay}
        </span>
      </div>
    );
  }

  // Otherwise, just render the avatar
  return avatarElement;
}
